import React__default from 'react';
import { Editor, Text, Transforms } from 'slate';
import { Type } from './types.mjs';
import { isValidProtocol, isSameLink, isBlockedProtocol, isValidTarget } from './utilities.mjs';
import { getNodeDeserializer } from '../../../../core/serialization/deserialize/utilities/getNodeDeserializer.mjs';
import { Link } from './components/Link/Link.mjs';
import { getRenderElement } from '../../../../core/rendering/getRenderElement.mjs';
import { getDerivedAttributes, getDerivedDataFromHTML } from '../../../../core/utilities/side-effects/combineSideEffects.mjs';
import { serialize } from '../../../../core/utilities/serialize/serialize.mjs';
import { getHotkeyHandler } from '../../../../core/utilities/handlers/hotkeys/getHotkeyHandler.mjs';
import { isElement } from '../../../../core/utilities/nodes/elements/isElement.mjs';

const hotkey = 'Ctrl+K';
const LinkPlugin = options => ({
  type: Type,
  hotkey,
  renderElement: getRenderElement({
    type: Type,
    rootProps: {},
    component(props) {
      const sideEffectAttributes = getDerivedAttributes({
        sideEffects: options == null ? void 0 : options.sideEffects,
        element: props.element
      });
      const combinedAttributes = {
        ...props.attributes,
        ...sideEffectAttributes
      };
      const sharedProps = {
        ...props,
        attributes: combinedAttributes,
        renderPopover: options == null ? void 0 : options.renderPopover
      };
      const element = props.element;
      if (element.url && isBlockedProtocol(element.url)) {
        return /*#__PURE__*/React__default.createElement(Link, Object.assign({}, sharedProps, {
          element: {
            ...element,
            url: undefined
          }
        }));
      }
      return /*#__PURE__*/React__default.createElement(Link, sharedProps);
    }
  }),
  serialize: {
    element(props) {
      const {
        children,
        element: {
          url,
          target,
          title
        }
      } = props;
      const href = url && isValidProtocol(url, options == null ? void 0 : options.allowedProtocols) ? url : undefined;
      const linkNodeData = {
        href,
        title,
        target
      };
      const sideEffectAttributes = getDerivedAttributes({
        sideEffects: options == null ? void 0 : options.sideEffects,
        element: props.element
      });
      const attributes = {
        ...sideEffectAttributes,
        ...linkNodeData
      };
      return serialize('a', children, attributes);
    }
  },
  deserialize: {
    element: getNodeDeserializer({
      type: Type,
      node: el => {
        const target = el.getAttribute('target');
        const linkNodeData = {
          type: Type,
          url: el.getAttribute('href'),
          title: el.getAttribute('title'),
          target: isValidTarget(target) ? target : null
        };
        return getDerivedDataFromHTML({
          sideEffects: options == null ? void 0 : options.sideEffects,
          data: linkNodeData,
          htmlElement: el
        });
      },
      rules: [{
        nodeNames: 'A',
        attribute: {
          href: href => isValidProtocol(href, options == null ? void 0 : options.allowedProtocols)
        }
      }]
    })
  },
  onKeyDown: options != null && options.onHotkey ? getHotkeyHandler(hotkey, options.onHotkey) : undefined,
  extendEditor(editor) {
    const {
      normalizeNode
    } = editor;
    editor.normalizeNode = entry => {
      const [node, path] = entry;

      // Combine identical adjacent links
      if (isElement(node) && node.type === Type) {
        for (const lookup of [Editor.previous, Editor.next]) {
          var _lookup;
          const [siblingNode, siblingPath] = (_lookup = lookup(editor, {
            at: path
          })) != null ? _lookup : [];
          const siblingIsSpacerText = Text.isText(siblingNode) && siblingNode.text === '';
          let adjacentNode = siblingNode;
          let adjacentNodePath = siblingPath;
          if (siblingIsSpacerText) {
            var _lookup2;
            const [secondSibling, secondSiblingPath] = (_lookup2 = lookup(editor, {
              at: siblingPath
            })) != null ? _lookup2 : [];
            adjacentNode = secondSibling;
            adjacentNodePath = secondSiblingPath;
          }
          if (adjacentNode && adjacentNodePath && isElement(adjacentNode) && adjacentNode.type === Type && isSameLink(node, adjacentNode)) {
            Editor.withoutNormalizing(editor, () => {
              if (!adjacentNodePath) {
                return;
              }
              const pathRef = Editor.pathRef(editor, lookup === Editor.previous ? path : adjacentNodePath);
              if (siblingIsSpacerText) {
                Transforms.removeNodes(editor, {
                  at: siblingPath
                });
              }
              if (!pathRef.current) {
                return;
              }
              Transforms.mergeNodes(editor, {
                at: pathRef.current
              });
            });
            return;
          }
        }
      }
      normalizeNode(entry);
    };
    return editor;
  },
  inlineTypes: [Type],
  removeEmptyTypes: [Type]
});

export { LinkPlugin, hotkey };
